// RiskChildrenTab — child risks linked to the current risk, with link/unlink.
// Data comes from GET /risks/{risk_id}/children.
// Parent-child relationship is stored via parent_risk_id column on child risk.

import { useState } from 'react'
import { useParams, useNavigate } from '@tanstack/react-router'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useApi } from '@/lib/api/client'
import { useTenant } from '@/components/shared/TenantShell'
import {
  risksApi,
  lifecycleStateLabels,
  lifecycleStateColors,
  type ChildRiskSummary,
  type EligibleChild,
} from '@/lib/api/risks'
import { queryKeys } from '@/lib/api/queryKeys'
import { ErrorState } from '@/components/shared/ErrorState'
import { TableSkeleton } from '@/components/shared/LoadingSkeleton'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import {
  GitBranch,
  ExternalLink,
  Plus,
  Unlink,
  FolderOpen,
  AlertTriangle,
  Search,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { useTranslation } from 'react-i18next'

export function RiskChildrenTab() {
  const { t } = useTranslation()
  const { slug } = useTenant()
  const { riskId } = useParams({ strict: false }) as { riskId: string }
  const navigate = useNavigate()
  const api = useApi()
  const queryClient = useQueryClient()
  const riskApi = risksApi(api)

  const [pickerOpen, setPickerOpen] = useState(false)
  const [unlinkTarget, setUnlinkTarget] = useState<ChildRiskSummary | null>(null)

  // Child risks
  const {
    data: children,
    error,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: queryKeys.risks.children(slug, riskId),
    queryFn: () => riskApi.children(riskId),
  })

  // Scoring staleness (loaded alongside children)
  const { data: scoringStatus } = useQuery({
    queryKey: queryKeys.risks.childScoringStatus(slug, riskId),
    queryFn: () => riskApi.childScoringStatus(riskId),
    enabled: (children?.length ?? 0) > 0,
  })

  // Unlink mutation
  const unlinkMutation = useMutation({
    mutationFn: (childId: string) => riskApi.unlinkChild(riskId, childId),
    onSuccess: () => {
      setUnlinkTarget(null)
      queryClient.invalidateQueries({ queryKey: queryKeys.risks.children(slug, riskId) })
      queryClient.invalidateQueries({ queryKey: queryKeys.risks.detail(slug, riskId) })
      queryClient.invalidateQueries({ queryKey: queryKeys.risks.childScoringStatus(slug, riskId) })
    },
  })

  if (isLoading) return <TableSkeleton rows={4} />

  if (error) {
    return (
      <div className="p-6">
        <ErrorState
          message={t('risks.children.loadError', 'Failed to load child risks')}
          technicalDetail={error.message}
          onRetry={() => refetch()}
        />
      </div>
    )
  }

  // Build staleness lookup
  const staleMap = new Map<string, boolean>()
  scoringStatus?.children?.forEach((c) => {
    staleMap.set(c.risk_id, c.is_stale)
  })

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <span>{children?.length ?? 0} child risk{children?.length !== 1 ? 's' : ''}</span>
          {scoringStatus?.has_stale_children && (
            <span className="flex items-center gap-1 text-amber-600">
              <AlertTriangle className="h-3.5 w-3.5" />
              <span className="text-xs">Some children have newer scoring</span>
            </span>
          )}
        </div>
        <Button variant="outline" size="sm" onClick={() => setPickerOpen(true)}>
          <Plus className="mr-1 h-4 w-4" />
          {t('risks.children.addButton', 'Add Child Risk')}
        </Button>
      </div>

      {/* Empty state */}
      {(!children || children.length === 0) && (
        <div className="flex h-48 items-center justify-center text-muted-foreground">
          <div className="space-y-2 text-center">
            <GitBranch className="mx-auto h-10 w-10 text-muted-foreground/40" />
            <p className="text-sm">{t('risks.children.empty', 'No child risks linked.')}</p>
            <p className="text-xs text-muted-foreground/60">
              {t('risks.children.emptyHint', 'Use the button above to link child risks from the same or descendant registers.')}
            </p>
          </div>
        </div>
      )}

      {/* Children list */}
      {children && children.length > 0 && (
        <div className="divide-y rounded-lg border">
          {children.map((child) => (
            <ChildRiskRow
              key={child.id}
              child={child}
              isStale={staleMap.get(child.id) ?? false}
              onClick={() => navigate({ to: `/t/${slug}/risks/${child.id}` })}
              onUnlink={() => setUnlinkTarget(child)}
            />
          ))}
        </div>
      )}

      {/* Add child picker */}
      <AddChildPickerDialog
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        riskId={riskId}
        slug={slug}
      />

      {/* Unlink confirmation */}
      {unlinkTarget && (
        <Dialog open={!!unlinkTarget} onOpenChange={() => setUnlinkTarget(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{t('risks.children.unlinkTitle', 'Remove Child Link')}</DialogTitle>
              <DialogDescription>
                {t('risks.children.unlinkDescription', 'Remove "{name}" as a child of this risk? The child risk will not be deleted.',
                  { name: unlinkTarget.name })}
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setUnlinkTarget(null)}>
                {t('common.cancel', 'Cancel')}
              </Button>
              <Button
                variant="destructive"
                disabled={unlinkMutation.isPending}
                onClick={() => unlinkMutation.mutate(unlinkTarget.id)}
              >
                {unlinkMutation.isPending
                  ? t('common.removing', 'Removing...')
                  : t('risks.children.unlinkConfirm', 'Remove Link')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  )
}

// --- Child row ---

function ChildRiskRow({
  child,
  isStale,
  onClick,
  onUnlink,
}: {
  child: ChildRiskSummary
  isStale: boolean
  onClick: () => void
  onUnlink: () => void
}) {
  return (
    <div
      className="group flex cursor-pointer items-center gap-4 px-4 py-3 transition-colors hover:bg-accent/30"
      onClick={onClick}
    >
      {/* Name */}
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="truncate text-sm font-medium">{child.name}</span>
          {isStale && (
            <span title="Scoring updated since parent">
              <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0 text-amber-500" />
            </span>
          )}
          <ExternalLink className="h-3 w-3 flex-shrink-0 text-muted-foreground opacity-0 group-hover:opacity-100" />
        </div>
      </div>

      {/* Register */}
      <div className="hidden items-center gap-1 text-xs text-muted-foreground md:flex">
        <FolderOpen className="h-3 w-3" />
        <span className="max-w-[120px] truncate">{child.register_name}</span>
      </div>

      {/* Score */}
      {child.residual_score != null && (
        <span className="flex-shrink-0 text-xs font-medium tabular-nums text-muted-foreground">
          Score: {child.residual_score}
        </span>
      )}

      {/* State badge */}
      <Badge
        variant="outline"
        className={cn(
          'flex-shrink-0 px-1.5 py-0 text-xs',
          lifecycleStateColors[child.lifecycle_state as keyof typeof lifecycleStateColors] ?? 'bg-gray-100 text-gray-700',
        )}
      >
        {lifecycleStateLabels[child.lifecycle_state as keyof typeof lifecycleStateLabels] ?? child.lifecycle_state}
      </Badge>

      {/* Unlink */}
      <button
        className="flex-shrink-0 rounded p-1 text-muted-foreground opacity-0 transition-opacity hover:bg-destructive/10 hover:text-destructive group-hover:opacity-100"
        onClick={(e) => {
          e.stopPropagation()
          onUnlink()
        }}
        title="Remove child link"
      >
        <Unlink className="h-4 w-4" />
      </button>
    </div>
  )
}

// --- Add child picker dialog ---

function AddChildPickerDialog({
  open,
  onClose,
  riskId,
  slug,
}: {
  open: boolean
  onClose: () => void
  riskId: string
  slug: string
}) {
  const { t } = useTranslation()
  const api = useApi()
  const queryClient = useQueryClient()
  const riskApi = risksApi(api)
  const [search, setSearch] = useState('')

  const { data: eligible, isLoading } = useQuery({
    queryKey: queryKeys.risks.eligibleChildren(slug, riskId),
    queryFn: () => riskApi.eligibleChildren(riskId),
    enabled: open,
  })

  const linkMutation = useMutation({
    mutationFn: (childId: string) => riskApi.linkChild(riskId, childId),
    onSuccess: () => {
      onClose()
      queryClient.invalidateQueries({ queryKey: queryKeys.risks.children(slug, riskId) })
      queryClient.invalidateQueries({ queryKey: queryKeys.risks.detail(slug, riskId) })
      queryClient.invalidateQueries({ queryKey: queryKeys.risks.eligibleChildren(slug, riskId) })
    },
  })

  const filtered = (eligible ?? []).filter((r) =>
    `${r.name} ${r.register_name} ${r.owner_display_name ?? ''}`.toLowerCase().includes(search.toLowerCase()),
  )

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-h-[80vh] max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('risks.children.pickerTitle', 'Add Child Risk')}</DialogTitle>
          <DialogDescription>
            {t('risks.children.pickerDescription', 'Select a risk from the same or descendant registers to link as a child.')}
          </DialogDescription>
        </DialogHeader>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('risks.children.searchPlaceholder', 'Search eligible risks...')}
            className="pl-9"
          />
        </div>

        {/* List */}
        <div className="max-h-[400px] overflow-y-auto rounded-md border">
          {isLoading && <div className="p-4 text-center text-sm text-muted-foreground">Loading...</div>}

          {!isLoading && filtered.length === 0 && (
            <div className="p-4 text-center text-sm text-muted-foreground">
              {search
                ? t('risks.children.noSearchResults', 'No matching risks found.')
                : t('risks.children.noEligible', 'No eligible risks available. Risks must have no existing parent and be in the same or descendant registers.')}
            </div>
          )}

          {filtered.map((risk) => (
            <EligibleChildRow
              key={risk.id}
              risk={risk}
              isPending={linkMutation.isPending}
              onLink={() => linkMutation.mutate(risk.id)}
            />
          ))}
        </div>
      </DialogContent>
    </Dialog>
  )
}

function EligibleChildRow({
  risk,
  isPending,
  onLink,
}: {
  risk: EligibleChild
  isPending: boolean
  onLink: () => void
}) {
  return (
    <div className="flex items-center gap-3 border-b px-3 py-2 last:border-b-0 hover:bg-accent/30">
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-medium">{risk.name}</div>
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <FolderOpen className="h-3 w-3" />
          <span className="truncate">{risk.register_name}</span>
          {risk.owner_display_name && (
            <>
              <span className="text-muted-foreground/40">·</span>
              <span className="truncate">{risk.owner_display_name}</span>
            </>
          )}
        </div>
      </div>
      <Badge
        variant="outline"
        className={cn(
          'flex-shrink-0 px-1.5 py-0 text-xs',
          lifecycleStateColors[risk.lifecycle_state as keyof typeof lifecycleStateColors] ?? 'bg-gray-100 text-gray-700',
        )}
      >
        {lifecycleStateLabels[risk.lifecycle_state as keyof typeof lifecycleStateLabels] ?? risk.lifecycle_state}
      </Badge>
      <Button
        variant="outline"
        size="sm"
        disabled={isPending}
        onClick={(e) => {
          e.stopPropagation()
          onLink()
        }}
      >
        <Plus className="mr-1 h-3 w-3" />
        Link
      </Button>
    </div>
  )
}
